import {ActionType} from '../../../actions/actions';
import {DisplayFeature, FeatureAttributeChange} from '../domain';
import FeaturesState from '../domain/FeaturesState';
import {replaceItem} from '../../../../array';
import {AnyAction} from 'redux';
import {FeatureTypeSummary} from '../../../../integration/representation/feature-type';
import {FeatureTypeComments, OrderedComments} from '../selectors';
import {ValidationIssue} from '../../../../integration/representation/validation-issue';

const InitialState = new FeaturesState();

const App = (state = InitialState, action: AnyAction): FeaturesState => {
    console.info(ActionType[action.type] || 'INITIALIZING');

    const actionType: ActionType = action.type;
    switch (actionType) {
        case ActionType.FEATURETAB3_SHOW_NVDB_IMPORT_MODAL:
            return state.withSelectedNvdbRoadObject(action.selectedNvdbRoadObject);
        case ActionType.FEATURETAB3_NAVIGATE:
            return state
                .withGridFocus(action.gridFocus)
                .withShowEditor(false)
                .withPreserveFocus(null);

        case ActionType.FEATURETAB3_SHOW_EDITOR:
            return state.withShowEditor(true);
        case ActionType.FEATURETAB3_CLOSE_EDITOR:
            return state.withShowEditor(false);
        case ActionType.FEATURETAB3_SORT_FEATURES:
            return state.withSortSetting(action.sortSetting)
                .withSelectedFeatures([]);
        case ActionType.FEATURETAB3_SET_SHOW_SUBMITTED_FEATURES:
            return state.withShowSubmittedFeatures(action.showSubmittedFeatures);
        case ActionType.FEATURETAB3_PRESERVE_FOCUS:
            return state.withPreserveFocus(action.preserveFocus);
        case ActionType.FEATURETAB3_ONLY_SHOW_OBJECTLIST_ATTRIBUTES:
            return state.withShowOnlyObjektlistAttributes(action.showOnlyObjektlistAttributes);
        case ActionType.FEATURETAB3_SET_SHOW_ONLY_SELECTD_FEATURES:
            return state.withShowOnlySelectedFeatures(action.showOnlySelectedFeatures);

        case ActionType.FEATURETAB3_SHOW_QUALITY_PARAMS:
            return state.withShowQualityParams(action.showQualityParams);

        case ActionType.FEATURETAB3_SET_TYPE_MODIFIED:
            // Long term
            const index = state.featureTypes.findIndex(ft => ft.typeid === action.typeId);
            if (index === -1) {
                console.warn('Type with ID ' + action.typeId + ' not found in feature type summary');
                return state;
            }
            const ft = state.featureTypes[index];
            if (ft.isDirty) {
                // No need for change
                return state;
            }

            const modifiedFt = {...ft, isDirty: true};

            const modifiedTypeSummary = replaceItem(state.featureTypes, index, modifiedFt);

            return state.withFeatureTypes(modifiedTypeSummary);

        case ActionType.FEATURETAB3_SET_MISSING_ATTRIBUTE_TYPES:
            return state.withMissingAttributeTypes(action.attributeTypes);

        case ActionType.FEATURETAB3_SET_FETCHING_MISSING_ATTRIBUTE_TYPES:
            return state.withFetchingMissingAttributeTypes(action.isFetching);
        case ActionType.FEATURETAB3_SET_FETCHING_SENSITIVE_ATTRIBUTE_TYPES:
            return state.withFetchingSensitiveAttributeTypes(action.isFetching);

        case ActionType.SELECT_CONTRACT:
            // We have an execution order problem after getting rid of the double loading bug
            // that we used to have with ngReact (NVDB-4638). Scene and ground 3D data is set
            // before this action is called, so we can't do a full reset without changing how
            // 3D loading works
            return new FeaturesState()
                .withContractId(action.contractId)
                .withGround3dData(state.ground3dData)
                .withSceneData(state.sceneData);
        case ActionType.REQUEST_CONTRACT:
        case ActionType.RESOLVE_ISSUE:
        case ActionType.FETCH_FEATURE_TYPE:
        case ActionType.LOCK_FEATURE:
        case ActionType.SHOW_EXISTING_FEATURES:
            return state.withIsFetching(true);
        case ActionType.RECEIVE_CONTRACT:
            return state.withIsFetching(false)
                .withFeatureTypes(updateFeatureSummariesFromComments(action.featureTypes, state.features, state.comments));
        case ActionType.RECEIVE_USER:
            return state.withUser(action.user);
        case ActionType.SELECT_FEATURE_TYPE:
            return state
                .withSelectedFeatures([])
                .withSelectedFeatureTypeId(action.featureTypeId)
                .withIsShowingExistingFeatures(false)
                .withExistingFeatures([])
                .withAdditionalSelectedFeatureType([]);
        case ActionType.UNSELECT_FEATURE_TYPE:
            return state
                .withNoSelectedFeatureType();
        case ActionType.RECEIVE_FEATURE_TYPE:
            return state
                .withIsFetching(false)
                .withSelectedFeatureType(action.featureType);
        case ActionType.ENABLE_GEOMETRY_EDIT:
            return state
                .withEditingGeometry(action.featureId);
        case ActionType.CLOSING_FEATURE_GEOMETRY_EDITOR:
            return state
                .withEditingGeometry(null);
        case ActionType.RECEIVE_ADDITIONAL_FEATURE_TYPE:
            return state
                .withIsFetching(false)
                .withAdditionalSelectedFeatureType(action.additionalSelectedFeatureTypes);
        case ActionType.SET_FEATURES:
            return state.withFeatures(action.features)
                .withFeatureTypes(addNewFeaturesToSummary(state.featureTypes ?? [], action.features));
        case ActionType.EDIT_ATTRIBUTE_LOCALLY:
            const change = action.change as FeatureAttributeChange;
            return state.withFeatures(state.features.map(feature => {
                if(feature.id === change.objectId) {
                    return {
                        ...feature,
                        attributes: feature.attributes.some(attribute => attribute.typeId === change.attributeTypeId)
                            ? feature.attributes.map(attribute => ({
                                ...attribute,
                                value: attribute.typeId === change.attributeTypeId ? change.newValue : attribute.value
                            }))
                            : [...feature.attributes, {
                                typeId: change.attributeTypeId,
                                value: change.newValue,
                                issues: {
                                    notabenes: [],
                                    warnings: [],
                                    errors: []
                                },
                                operation: "WRITE"
                            }]
                    };
                }
                return feature;
            }));
        case ActionType.FEATURES_DELETED:
            return state
                .withFeatures(state.features.filter(existingFeature => !action.featureIds?.includes(existingFeature.id)));
        case ActionType.ISSUES_RESOLVED:
        case ActionType.ERROR:
            return state.withIsFetching(false);
        case ActionType.SET_SELECTED_FEATURES:
            return state.withSelectedFeatures(action.featureIds);
        case ActionType.SELECT_FEATURES:
            return state.withSelectedFeatures(state.selectedFeatures.concat(action.featureIds));
        case ActionType.UNSELECT_FEATURES:
            return state.withSelectedFeatures(state.selectedFeatures
                .filter(fid => action.featureIds.indexOf(fid) === -1));
        case ActionType.FEATURETAB3_DESELECT_ALL_FEATURES:
            return state.withSelectedFeatures([]);
        case ActionType.REMOVE_EXISTING_FEATURES:
            return state.withIsShowingExistingFeatures(false);
        case ActionType.RECEIVE_CLOSINGS:
            return state.withClosings(action.closings);
        case ActionType.RECEIVE_EXISTING_FEATURES:
            return state
                .withIsFetching(false)
                .withExistingFeatures(action.existingFeatures);
        case ActionType.ENABLE_SHOW_EXISTING_FEATURES:
            return state.withIsShowingExistingFeatures(true);
        case ActionType.TOGGLE_FULLSCREEN:
            return state.toggleFullscreen();
        case ActionType.TOGGLE_SPLITSCREEN:
            return state.toggleSplitScreen();
        case ActionType.GO_HOME:
            return new FeaturesState();
        case ActionType.CHANGE_SCENE_DATA:
            return state.withSceneData(action.sceneData);
        case ActionType.CHANGE_GROUND3D_DATA:
            return state.withGround3dData(action.ground3dData);
        case ActionType.FETCH_DATACATALOG:
            return state.withDatacatalog(action.datacatalog);
        case ActionType.START_FETCHING:
            return state.withIsFetching(true);
        case ActionType.STOP_FETCHING:
            return state.withIsFetching(false);
        case ActionType.SET_SHOW_DESCRIPTION_INSTEAD_OF_NAME:
            return state.withUseDescriptiveName(action.useDescriptiveName);
        case ActionType.SET_POPUP:
            return state.withPopup(action.popup);
        case ActionType.FEATURETAB3_SET_COPY_GEOMETRY_MODAL_VISIBLE:
            return state.withCopyGeometryModalVisible(action.isVisible);
        case ActionType.URL_HAS_SENSITIVE_ATTRIBUTES:
            return state.withSensitiveAttributesInURL(action.hasSensitiveAttributes);
        case ActionType.FEATURETAB3_SET_COPIABLE_FEATURES:
            return state.withCopiableGeometries(action.features);
        case ActionType.FEATURETAB3_SET_FETCHING_COPIABLE_FEATURES:
            return state.withFetchingCopiableGeometries(action.fetching);
        case ActionType.FEATURETAB3_SET_COPYING_GEOMETRY_FEATURES:
            return state.withCopyingGeometriesBetweenFeatures(action.copying);
        case ActionType.SET_ROAD_SYSTEM_REFERENCES:
            return state.withRoadSystemReferences({...state.roadSystemReferences, ...action.refs});
        case ActionType.SET_COMMENTS:
            return state.withComments(action.comments)
                        .withFeatures(addFeatureIssuesFromComments(state.features, action.comments))
                        .withFeatureTypes(updateFeatureSummariesFromComments(state.featureTypes ?? [], state.features ?? [], action.comments));
        case ActionType.FEATURE_TAB3_SET_COMMENTS_MODAL_VISIBLE:
            return state.withCommentsModalVisible(action.visible);
        case ActionType.FEATURE_TAB3_SET_COMMENTS_FILTER:
            return state.withCommentsFilter(action.filter).withMarkedComments([]);
        case ActionType.FEATURE_TAB3_SET_MARKED_COMMENTS:
            return state.withMarkedComments(action.markedComments);
        case ActionType.FEATURE_TAB3_SET_FEATURE_OR_TYPE_FOR_COMMENTS:
            return state.withFeatureOrTypeIdForComments(action.featureOrTypeId);
        case ActionType.FEATURE_TAB3_SET_ROAD_REFS:
            return state.withRoadSegments(action.roadSegments);
        case ActionType.FEATURE_TAB3_SET_SELECTED_ROAD_REF:
            return state.withSelectedRoadSegment(action.selectedRoadSegment);
        case ActionType.SET_LOADING_ATTRIBUTES:
            return state.withLoadingAttributes(action.loadingAttributes);
        case ActionType.SET_LOADING_SPECIAL_ATTRIBUTES:
            return state.withLoadingSpecialAttributes(action.loadingSpecialAttributes);
        case ActionType.FEATURE_TAB3_SET_CREATE_OBJECT_VISIBLE:
            return state.withCreateObjectVisible(action.visible);
        case ActionType.FEATURE_TAB3_SET_CREATE_OBJECT_TYPE_ID:
            return state.withCreateObjectTypeId(action.id);
        case ActionType.FEATURE_TAB3_SET_CREATE_OBJECT_WKT:
            return state.withCreateObjectWkt(action.wkt);
        case ActionType.FEATURE_TAB3_SET_REFERENCE_ERROR:
            return state.withSetReferenceError(action.error);
        case ActionType.FEATURE_TAB3_SET_CREATING_OBJECT_GEOMETRY:
            return state.withCreatingObjectGeometry(action.creating);
        case ActionType.SET_FEATURES_PAGE:
            return state.withFeaturesPage(action.page)
                .withSelectedFeatures([]);
        case ActionType.SET_FEATURES_PAGE_SIZE:
            return state
                .withFeaturesPageSize(action.pageSize)
                .withFeaturesPage(0)
                .withSelectedFeatures([]);
        case ActionType.SET_FEATURE_SEARCH:
            return state
                .withFeatureSearchString(action.searchString)
                .withFeaturesPage(0)
                .withSelectedFeatures([]);
        default:
            return state;
    }
};

const addNewFeaturesToSummary = (existingSummaries: FeatureTypeSummary[], newFeatures: DisplayFeature[]): FeatureTypeSummary[] => {
    return existingSummaries.map(summary => {
        const featuresForSummary = newFeatures.filter(feature => feature.typeId === summary.typeid);
        if (featuresForSummary.length === 0) {
            return summary;
        }
        return {
            ...summary,
            numFeatureComments: featuresForSummary.reduce((prev, curr) => prev + curr.comments.length, 0),
            aggregatedErrors: {
                numError: featuresForSummary.reduce((prev, curr) => prev + curr.issues.errors.filter(error => error.display).length, 0),
                numWarning: featuresForSummary.reduce((prev, curr) => prev + curr.issues.warnings.filter(warning => warning.display).length, 0),
                numNotabene: featuresForSummary.reduce((prev, curr) => prev + curr.issues.notabenes.filter(notabene => notabene.display).length, 0)
            }
        };
    });
};

const updateFeatureSummariesFromComments = (existingSummaries: FeatureTypeSummary[], existingFeatures: DisplayFeature[], newComments: OrderedComments): FeatureTypeSummary[] => {
    return existingSummaries.map(summary => {
        const commentsForSummary: FeatureTypeComments = newComments[summary.typeid];
        if (!commentsForSummary) {
            return summary;
        }
        const existingErrors = existingFeatures
            .map(features => features.issues.errors)
            .reduce((prev, curr) => prev.concat(curr))
            .filter(error => error.code !== 'ISSUE_ADDED_BY_OPERATOR')
            .length;
        const featureCommentErrors = Object.values(commentsForSummary.features)
            .map(feature => feature.comments.filter(comment => comment.error))
            .reduce((prev, curr) => prev.concat(curr), []).length;
        const featureTypeCommentErrors = Object.values(commentsForSummary.comments)
            .filter(comment => comment.error)
            .reduce((prev, curr) => prev.concat(curr), []).length;
        const newErrors = existingErrors + featureCommentErrors + featureTypeCommentErrors;
        return {
            ...summary,
            numFeatureTypeComments: commentsForSummary.comments.length,
            numFeatureComments: Object.values(commentsForSummary.features).reduce((prev, feature) => prev + feature.comments.length, 0),
            aggregatedErrors: {
                ...summary.aggregatedErrors,
                numError: newErrors
            }
        };
    });
};

const addFeatureIssuesFromComments = (existingFeatures: DisplayFeature[], newComments: OrderedComments): DisplayFeature[] => {
    return existingFeatures.map(feature => {
        const topLevelComments = newComments[feature.typeId];
        if (!topLevelComments) {
            return feature;
        }
        const commentsForFeature = topLevelComments.features[feature.id];
        if (!commentsForFeature) {
            return feature;
        }
        const newIssues: ValidationIssue[] = commentsForFeature.comments.filter(comment => comment.error)
            .map(comment => ({
                id: comment.id,
                code: 'ISSUE_ADDED_BY_OPERATOR',
                severity: 'ERROR',
                message: comment.comment,
                location: {
                    contractId: comment.contractId,
                    commentId: comment.id
                },
                resolution: null,
                display: true
            }));
        return {
            ...feature,
            issues: {
                ...feature.issues,
                errors: [...feature.issues.errors.filter(error => error.code !== 'ISSUE_ADDED_BY_OPERATOR'), ...newIssues]
            }
        };
    });
};

export default App;
